import React, { useMemo, memo } from 'react';
import { ReportData } from '../../types/report.types';

interface CoverPageEditorProps {
  reportData: ReportData;
  onInputChange: (field: keyof ReportData, value: string) => void;
}

type CoverPageField =
  | 'report_title'
  | 'company_name'
  | 'program_name'
  | 'document_number'
  | 'version_number'
  | 'current_date'
  | 'revision_date'
  | 'start_date'
  | 'end_date'
  | 'testing_type'
  | 'client_logo';

interface FormField {
  id: CoverPageField;
  label: string;
  type: 'text' | 'date';
  placeholder?: string;
  required?: boolean;
}

const formFields: FormField[] = [
  { id: 'report_title', label: 'Report Title', type: 'text', placeholder: 'Enter report title', required: true },
  { id: 'program_name', label: 'Program Name', type: 'text', placeholder: 'Enter program name for cover page' },
  { id: 'company_name', label: 'Company Name', type: 'text', placeholder: 'Enter company name', required: true },
  { id: 'document_number', label: 'Document Number', type: 'text', placeholder: 'Enter document number' },
  { id: 'version_number', label: 'Version Number', type: 'text', placeholder: 'Enter version number' },
  { id: 'current_date', label: 'Current Date', type: 'date', required: true },
  { id: 'revision_date', label: 'Revision Date', type: 'date' },
  { id: 'start_date', label: 'Testing Period Start Date', type: 'date' },
  { id: 'end_date', label: 'Testing Period End Date', type: 'date' },
  { id: 'testing_type', label: 'Pentest Type (e.g. Grey-box Web Application)', type: 'text', placeholder: 'e.g. Grey-box Web Application Penetration Test' },
  { id: 'client_logo', label: "Client Logo URL (Organization Assessed)", type: 'text', placeholder: 'Enter client logo URL' },
];

const getAutoPentestType = (reportData: any) => {
  const targets = reportData.target_details || [];
  if (targets.length === 0) {
    return 'Grey-Box Web Application Penetration Test';
  }

  const types = new Set<string>();
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  targets.forEach((t: any) => {
    const typeStr = (t.target_type || t.type || '').toLowerCase();
    if (isNetworkTarget(t)) {
      types.add('Network Infrastructure');
    } else if (typeStr.includes('api')) {
      types.add('API');
    } else if (typeStr.includes('mobile') || typeStr.includes('ios') || typeStr.includes('android')) {
      types.add('Mobile Application');
    } else {
      types.add('Web Application');
    }
  });

  const joinedTypes = Array.from(types).join(' & ');
  return `Grey-Box ${joinedTypes} Penetration Test`;
};

const CoverPageEditor: React.FC<CoverPageEditorProps> = memo(({ reportData, onInputChange }) => {
  const handleInputChange = (field: CoverPageField, value: string) => {
    onInputChange(field, value);
  };

  const autoPentestType = useMemo(() => getAutoPentestType(reportData), [reportData.target_details]);

  const renderField = (field: FormField) => {
    const labelClasses = "block text-xs font-medium text-gray-700 mb-0.5";
    let fieldValue = (reportData[field.id] as string) || '';
    if (field.type === 'date' && fieldValue) {
      try {
        fieldValue = new Date(fieldValue).toISOString().split('T')[0];
      } catch (e) {}
    }
    return (
      <div key={field.id} className="flex flex-col mb-2">
        <label htmlFor={field.id} className={labelClasses}>
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <input
          id={field.id}
          type={field.type}
          value={fieldValue}
          onChange={(e) => handleInputChange(field.id, e.target.value)}
          className="w-full px-2.5 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-200 placeholder:text-slate-400"
          placeholder={field.id === 'testing_type' ? autoPentestType : field.placeholder}
          required={field.required}
          aria-required={field.required}
        />
      </div>
    );
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">Cover Page</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">Edit cover page details</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          {/* First column: Report Title, Program Name, Company Name, Document Number */}
          <div className="flex flex-col gap-2">
            {renderField(formFields[0])} {/* Report Title */}
            {renderField(formFields[1])} {/* Program Name */}
            {renderField(formFields[2])} {/* Company Name */}
            {renderField(formFields[3])} {/* Document Number */}
            {reportData.template === 'template-2' && renderField(formFields[10])} {/* Client Logo URL */}
            {reportData.template === 'template-2' && reportData.client_logo && (
              <div className="mt-1">
                <span className="block text-[10px] text-gray-500 mb-0.5">Client Logo Preview:</span>
                <div className="bg-gray-50 border border-slate-200 rounded-md p-1.5 flex items-center justify-center min-h-[50px]">
                  <img
                    src={reportData.client_logo}
                    alt="Client Logo Preview"
                    className="max-h-12 max-w-full object-contain"
                  />
                </div>
              </div>
            )}
          </div>
          {/* Second column: Version Number, Dates side by side */}
          <div className="flex flex-col gap-2">
            {renderField(formFields[4])} {/* Version Number */}
            <div className="grid grid-cols-2 gap-2">
              {renderField(formFields[5])} {/* Current Date */}
              {renderField(formFields[6])} {/* Revision Date */}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {renderField(formFields[7])} {/* Testing Period Start Date */}
              {renderField(formFields[8])} {/* Testing Period End Date */}
            </div>
            {reportData.template === 'template-2' && renderField(formFields[9])} {/* Pentest Type */}
          </div>
        </div>
      </div>
    </div>
  );
});

export default CoverPageEditor; 